import React, { useState, useEffect } from 'react';

const AIQuizGenerationSlide: React.FC = () => {
  const [animationStep, setAnimationStep] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [generatedQuestions, setGeneratedQuestions] = useState<number>(0);

  const inputText = "World War II key battles";
  const maxQuestions = 5;

  useEffect(() => {
    // Step 1: Type the input text (0-1.5s)
    if (animationStep === 0) {
      let currentChar = 0;
      const typingInterval = setInterval(() => {
        if (currentChar <= inputText.length) {
          setTypedText(inputText.slice(0, currentChar));
          currentChar++;
        } else {
          clearInterval(typingInterval);
          setTimeout(() => setAnimationStep(1), 500);
        }
      }, 80);
      return () => clearInterval(typingInterval);
    }

    // Step 2: Show "AI Generating..." state (1.5s-2.5s)
    if (animationStep === 1) {
      setTimeout(() => setAnimationStep(2), 1000);
    }

    // Step 3: Generate questions one by one (2.5s onwards)
    if (animationStep === 2) {
      const questionInterval = setInterval(() => {
        setGeneratedQuestions((prev) => {
          if (prev < maxQuestions) {
            return prev + 1;
          }
          clearInterval(questionInterval);
          // Loop back after showing complete quiz
          setTimeout(() => {
            setAnimationStep(0);
            setTypedText('');
            setGeneratedQuestions(0);
          }, 3000);
          return prev;
        });
      }, 600);
      return () => clearInterval(questionInterval);
    }
  }, [animationStep]);

  const questions = [
    { id: 1, text: "What year did D-Day take place?", difficulty: "Medium" },
    { id: 2, text: "Which battle marked the turning point in the Pacific?", difficulty: "Hard" },
    { id: 3, text: "What was the significance of Stalingrad?", difficulty: "Medium" },
    { id: 4, text: "Who commanded Allied forces at Normandy?", difficulty: "Easy" },
    { id: 5, text: "What was Operation Market Garden?", difficulty: "Hard" }
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <div className="max-w-5xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 7H7v6h6V7z" />
              <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
            </svg>
            AI-Powered Quiz Generation
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            From Topic to Quiz in <span className="text-orange-500">Seconds</span>
          </h2>
          <p className="text-xl text-gray-600">
            Just describe your topic. AI handles the rest.
          </p>
        </div>

        {/* Input Section */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-8 mb-6">
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              What topic should the quiz cover?
            </label>
            <div className="relative">
              <input
                type="text"
                value={typedText}
                readOnly
                className="w-full px-4 py-3 border-2 border-orange-300 rounded-lg text-lg focus:outline-none focus:border-orange-500 transition-colors"
                placeholder="e.g., World War II key battles, Photosynthesis, Spanish verbs..."
              />
              {animationStep === 0 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-orange-500 animate-pulse"></div>
              )}
            </div>
          </div>

          {/* AI Status */}
          {animationStep >= 1 && (
            <div className="flex items-center gap-3 mb-6 animate-fadeIn">
              <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 rounded-full">
                {animationStep === 1 ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span className="font-semibold">AI Generating Questions...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">{generatedQuestions} of {maxQuestions} Questions Generated</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Generated Questions */}
          {animationStep === 2 && (
            <div className="space-y-3">
              {questions.slice(0, generatedQuestions).map((question, index) => (
                <div
                  key={question.id}
                  className="bg-gradient-to-r from-gray-50 to-orange-50 border-l-4 border-orange-500 p-4 rounded-lg transform transition-all duration-300 hover:shadow-md"
                  style={{
                    animation: 'slideIn 0.4s ease-out',
                    animationFillMode: 'both'
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded">
                          Q{question.id}
                        </span>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${
                          question.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                          question.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {question.difficulty}
                        </span>
                      </div>
                      <p className="text-gray-900 font-medium">{question.text}</p>
                    </div>
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Features List */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <div className="text-3xl mb-2">⚡</div>
            <h3 className="font-semibold text-gray-900 mb-1">Lightning Fast</h3>
            <p className="text-sm text-gray-600">Complete quizzes in under 10 seconds</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <div className="text-3xl mb-2">🎯</div>
            <h3 className="font-semibold text-gray-900 mb-1">Curriculum-Aligned</h3>
            <p className="text-sm text-gray-600">Questions matched to learning objectives</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <div className="text-3xl mb-2">💡</div>
            <h3 className="font-semibold text-gray-900 mb-1">With Explanations</h3>
            <p className="text-sm text-gray-600">Every answer includes detailed reasoning</p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default AIQuizGenerationSlide;
